import { useSDKConfigStore } from "../stores/sdkConfigStore";

// Auth state interface
interface AuthState {
  signature: string | null;
  deadline: number | null;
}

// Global auth state
let authState: AuthState = {
  signature: null,
  deadline: null,
};

// EIP712 Typed Data for login
const createLoginTypedData = (eoaAddress: string, timestamp: number) => ({
  types: {
    EIP712Domain: [
      { name: "name", type: "string" },
      { name: "version", type: "string" },
    ],
    LoginRequest: [
      { name: "eoa", type: "address" },
      { name: "message", type: "string" },
      { name: "timestamp", type: "uint256" },
      { name: "action", type: "string" },
    ],
  },
  primaryType: "LoginRequest",
  domain: {
    name: "BrahmaCards",
    version: "1",
  },
  message: {
    eoa: eoaAddress,
    message: "I'm a BrahmaCards user",
    timestamp: timestamp,
    action: "login",
  },
});

// Generate auth signature
const generateAuthSignature = async (
  eoaAddress: string,
  signTypedData: (typedData: any) => Promise<string>
): Promise<{ signature: string; deadline: number }> => {
  // Set deadline to current time + 1 hour
  const deadline = Math.floor(Date.now() / 1000) + 3600; // 1 hour in seconds

  const typedData = createLoginTypedData(eoaAddress, deadline);
  const signature = await signTypedData(typedData);

  console.log({ typedData, deadline, signature, type: typeof signature });

  return { signature, deadline };
};

// Export the generateAuthSignature function for external use
export const generateAuthSign = async (
  eoaAddress: string,
  signTypedData: (typedData: any) => Promise<string>
): Promise<{ signature: string; deadline: number }> => {
  return generateAuthSignature(eoaAddress, signTypedData);
};

// Initialize authentication with pre-generated signature
export async function initializeAuth(
  eoaAddress: string,
  authSignature: { signature: string; deadline: number }
): Promise<void> {
  try {
    authState = {
      signature: authSignature.signature,
      deadline: authSignature.deadline,
    };

    console.log("SwypeSDK auth initialized");
  } catch (error) {
    console.error("Failed to initialize SwypeSDK auth:", error);
    throw error;
  }
}

// Check if auth is expired and throw error if needed (no auto-renewal)
export const ensureValidAuth = async (): Promise<void> => {
  const { eoaAddress, isInitialized } = useSDKConfigStore.getState();

  if (!isInitialized || !eoaAddress) {
    throw new Error("SwypeSDK not initialized. Call initializeSwypeSDK first.");
  }

  const currentTime = Math.floor(Date.now() / 1000);

  // If no auth or auth is expired, throw error since we can't auto-renew
  if (
    !authState.signature ||
    !authState.deadline ||
    authState.deadline <= currentTime
  ) {
    throw new Error(
      "Authentication expired. Please re-initialize the SDK with a new signature."
    );
  }
};

// Get current auth state
export const getAuthState = (): AuthState => {
  return { ...authState };
};

// Clear auth state (useful for logout or auth errors)
export const clearAuth = (): void => {
  authState = {
    signature: null,
    deadline: null,
  };
};
